<script lang="ts">
  import type { BusinessFAQItem } from "./_logic/types"
  import { onMount } from "svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"

  type Props = {
    sectionId?: string
    subtitle?: string
    title: string
    description?: string
    items?: BusinessFAQItem[]
    brightBackground?: boolean
    defaultOpenCount?: number
    mobileDefaultOpenCount?: number | null
  }

  let {
    sectionId = "faq",
    subtitle = "FAQ",
    title,
    description = "",
    items = [],
    brightBackground = true,
    defaultOpenCount = Number.POSITIVE_INFINITY,
    mobileDefaultOpenCount = null,
  }: Props = $props()

  let responsiveOpenCount = $state<number | null>(null)
  const initialOpenCount = $derived(responsiveOpenCount ?? defaultOpenCount)

  onMount(() => {
    if (mobileDefaultOpenCount === null || typeof window.matchMedia !== "function") return

    if (window.matchMedia("(max-width: 760px)").matches) {
      responsiveOpenCount = mobileDefaultOpenCount
    }
  })
</script>

<section class="mini-faq-section" id={sectionId} data-theme={brightBackground ? "light" : "dark"}>
  <SectionTitle {subtitle} {title} {description} {brightBackground} />

  <div class="faq-grid">
    {#each items as item, index (item.question)}
      <details class="faq-item" open={index < initialOpenCount}>
        <summary>
          <span class="question">{item.question}</span>
          <span class="chevron" aria-hidden="true"></span>
        </summary>
        <div class="answer">
          <p>{item.answer}</p>
        </div>
      </details>
    {/each}
  </div>
</section>

<style lang="less">
  .mini-faq-section {
    --faq-border: rgba(13, 12, 12, 0.32);
    --faq-surface: white;
    --faq-title: var(--text-invers-100);
    --faq-copy: var(--text-invers-200);
    --faq-corner-size: 1.55rem;

    display: grid;
    gap: 1.5rem;

    &[data-theme="dark"] {
      --faq-border: rgba(243, 238, 217, 0.48);
      --faq-surface: var(--bg-100);
      --faq-title: var(--text-100);
      --faq-copy: var(--text-200);
    }
  }

  .faq-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .faq-item {
    position: relative;
    overflow: hidden;
    border: 1px solid var(--faq-border);
    background: var(--faq-surface);
    clip-path: polygon(
      0 0,
      calc(100% - var(--faq-corner-size)) 0,
      100% var(--faq-corner-size),
      100% 100%,
      0 100%
    );

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: var(--faq-corner-size);
      height: var(--faq-corner-size);
      background: linear-gradient(
        to top right,
        transparent calc(50% - 0.5px),
        var(--faq-border) calc(50% - 0.5px),
        var(--faq-border) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      );
      pointer-events: none;
    }
  }

  summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.9rem;
    min-height: 3.55rem;
    padding: 0.95rem calc(var(--faq-corner-size) + 0.95rem) 0.8rem 1rem;
    color: var(--faq-title);
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  .question {
    font-size: 1.08rem;
    line-height: 1.12;
    font-weight: 800;
  }

  .chevron {
    width: 0.55rem;
    height: 0.55rem;
    border-right: 2px solid var(--faq-title);
    border-bottom: 2px solid var(--faq-title);
    transform: rotate(45deg);
    transform-origin: center;
    transition: transform 0.18s ease;
  }

  .faq-item[open] .chevron {
    transform: translateY(0.18rem) rotate(225deg);
  }

  .answer {
    padding: 1.05rem 1rem 1.15rem;
    border-top: 1px solid var(--faq-border);
  }

  p {
    margin: 0;
    color: var(--faq-copy);
    line-height: 1.5;
  }

  @media (max-width: 760px) {
    .faq-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
